import React, { useEffect, useMemo, useState } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, orderBy, query } from '../../firebase.config';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const months = [
  { value: '04', label: 'April' }, { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' }, { value: '09', label: 'September' },
  { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' },
  { value: '01', label: 'January' }, { value: '02', label: 'February' }, { value: '03', label: 'March' }
];

const quarters = [
  { value: 'Q1', label: 'Q1 (Apr-Jun)' },
  { value: 'Q2', label: 'Q2 (Jul-Sep)' },
  { value: 'Q3', label: 'Q3 (Oct-Dec)' },
  { value: 'Q4', label: 'Q4 (Jan-Mar)' }
];

export default function TaxPaymentsTab({ db, userId, appId, companyId, financialYear, companyDetails }) {
  const [entries, setEntries] = useState([]);
  const [taxType, setTaxType] = useState('GST');
  const [periodType, setPeriodType] = useState('month');
  const [periodValue, setPeriodValue] = useState('04');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('Bank Transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  // Using getCollectionForCompany instead of legacy path

  const fySuffix = useMemo(() => {
    const yr = financialYear % 100; return `${financialYear}-${(financialYear + 1).toString().slice(-2)}`;
  }, [financialYear]);

  const fetchEntries = async () => {
    const taxPaymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'taxPayments');
    const snap = await getDocs(query(taxPaymentsRef, orderBy('paymentDate', 'desc')));
    setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchEntries(); }, [db, userId, appId, financialYear]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const taxPaymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'taxPayments');
    await addDoc(taxPaymentsRef, {
      taxType,
      fy: fySuffix,
      periodType,
      periodValue,
      paymentDate,
      amount: Number(amount||0),
      paymentMode,
      reference,
      notes,
      createdAt: new Date().toISOString()
    });
    setAmount(''); setReference(''); setNotes('');
    fetchEntries();
  };

  const handleDelete = async (id) => { 
    const taxPaymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'taxPayments');
    await deleteDoc(doc(taxPaymentsRef, id)); 
    fetchEntries(); 
  };

  return (
    <div className="p-2 sm:p-6">
      <div className="mb-3 sm:mb-4">
        <h2 className="text-lg sm:text-xl font-semibold">Tax Payments</h2>
        <div className="text-xs sm:text-sm text-gray-600">Financial Year: {fySuffix}</div>
      </div>

      <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 bg-gray-50 p-3 sm:p-4 rounded border mb-4 sm:mb-6">
        <div>
          <label className="block text-xs sm:text-sm mb-1">Tax Type</label>
          <AppSelect
            value={taxType}
            onChange={e=>setTaxType(e.target.value)}
            aria-label="Tax type"
            sheetTitle="Tax type"
            options={[
              { value: 'GST', label: 'GST' },
              { value: 'TDS', label: 'TDS' },
              { value: 'ITR', label: 'ITR' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded px-2 sm:px-3 text-xs sm:text-sm`}
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm mb-1">Period Type</label>
          <AppSelect
            value={periodType}
            onChange={e=>setPeriodType(e.target.value)}
            aria-label="Period type"
            sheetTitle="Period type"
            options={[
              { value: 'month', label: 'Month' },
              { value: 'quarter', label: 'Quarter' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded px-2 sm:px-3 text-xs sm:text-sm`}
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm mb-1">{periodType === 'month' ? 'Month' : 'Quarter'}</label>
          {periodType === 'month' ? (
            <AppSelect
              value={periodValue}
              onChange={e=>setPeriodValue(e.target.value)}
              aria-label="Month"
              sheetTitle="Month"
              options={months.map(m => ({
                value: `${m.value}-${String(financialYear).slice(-2)}`,
                label: m.label,
              }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded px-2 sm:px-3 text-xs sm:text-sm`}
            />
          ) : (
            <AppSelect
              value={periodValue}
              onChange={e=>setPeriodValue(e.target.value)}
              aria-label="Quarter"
              sheetTitle="Quarter"
              options={quarters.map(q => ({
                value: `${q.value}-${String(financialYear).slice(-2)}`,
                label: q.label,
              }))}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded px-2 sm:px-3 text-xs sm:text-sm`}
            />
          )}
        </div>
        <div>
          <label className="block text-xs sm:text-sm mb-1">Payment Date</label>
          <input type="date" value={paymentDate} onChange={e=>setPaymentDate(e.target.value)} className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm" />
        </div>
        <div>
          <label className="block text-xs sm:text-sm mb-1">Amount</label>
          <input type="number" value={amount} onChange={e=>setAmount(e.target.value)} className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm" />
        </div>
        <div>
          <label className="block text-xs sm:text-sm mb-1">Payment Mode</label>
          <AppSelect
            value={paymentMode}
            onChange={e=>setPaymentMode(e.target.value)}
            aria-label="Payment mode"
            sheetTitle="Payment mode"
            options={[
              { value: 'Cash', label: 'Cash' },
              { value: 'UPI', label: 'UPI' },
              { value: 'Cheque/DD', label: 'Cheque/DD' },
              { value: 'Bank Transfer', label: 'Bank Transfer' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded px-2 sm:px-3 text-xs sm:text-sm`}
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm mb-1">Challan/Reference No</label>
          <input value={reference} onChange={e=>setReference(e.target.value)} className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm" />
        </div>
        <div className="sm:col-span-2 lg:col-span-1">
          <label className="block text-xs sm:text-sm mb-1">Notes</label>
          <input value={notes} onChange={e=>setNotes(e.target.value)} className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm" />
        </div>
        <div className="flex items-end sm:col-span-2 lg:col-span-1">
          <button type="submit" className="w-full bg-blue-600 text-white px-3 sm:px-4 py-2 rounded text-xs sm:text-sm">Add Payment</button>
        </div>
      </form>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm bg-white rounded border">No entries</div>
        ) : entries.map((e) => (
          <div key={e.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex justify-between items-start gap-2 mb-2">
              <div>
                <div className="font-semibold text-gray-900">{e.taxType}</div>
                <div className="text-xs text-gray-500">{e.paymentDate}</div>
              </div>
              <div className="text-right font-bold text-blue-700 tabular-nums">
                ₹{Number(e.amount || 0).toLocaleString('en-IN')}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
              <div><span className="text-gray-400">Period:</span> {e.periodValue}</div>
              <div><span className="text-gray-400">Mode:</span> {e.paymentMode}</div>
              {e.reference && <div className="col-span-2 break-all"><span className="text-gray-400">Ref:</span> {e.reference}</div>}
            </div>
            <button
              onClick={() => handleDelete(e.id)}
              className="w-full py-2.5 text-sm text-red-600 bg-red-50 rounded-lg touch-manipulation min-h-[44px]"
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      <div className="hidden md:block bg-white rounded border report-scroll-x">
        <table className="min-w-full text-xs sm:text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-4 py-2 text-left">Date</th>
              <th className="px-2 sm:px-4 py-2 text-left">Tax</th>
              <th className="px-2 sm:px-4 py-2 text-left hidden sm:table-cell">Period</th>
              <th className="px-2 sm:px-4 py-2 text-left hidden md:table-cell">Mode</th>
              <th className="px-2 sm:px-4 py-2 text-right">Amount</th>
              <th className="px-2 sm:px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id} className="border-t">
                <td className="px-2 sm:px-4 py-2">{e.paymentDate}</td>
                <td className="px-2 sm:px-4 py-2">{e.taxType}</td>
                <td className="px-2 sm:px-4 py-2 hidden sm:table-cell">{e.periodType === 'month' ? e.periodValue : e.periodValue}</td>
                <td className="px-2 sm:px-4 py-2 hidden md:table-cell">{e.paymentMode}</td>
                <td className="px-2 sm:px-4 py-2 text-right">{Number(e.amount||0).toLocaleString('en-IN')}</td>
                <td className="px-2 sm:px-4 py-2 text-right"><button onClick={()=>handleDelete(e.id)} className="text-red-600 text-xs sm:text-sm">Delete</button></td>
              </tr>
            ))}
            {entries.length === 0 && (<tr><td className="px-2 sm:px-4 py-6 text-gray-500 text-center" colSpan={6}>No entries</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}


